import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header";
import { useEffect, useState } from "react";
import { fetchPlans, Plan } from "../../../api/useplan";
import {
  fetchPlanWidgetCounts,
  PlanWidgetCounts,
} from "../../../api/usermanager";
import { Pagination } from "antd";

const PackagesGrid = () => {
  const [filteredPlans, setFilteredPlans] = useState<Plan[]>([]);
  const [selectedPlanType, setSelectedPlanType] = useState<string>("All");
  const [widgetCounts, setWidgetCounts] = useState<PlanWidgetCounts | null>(
    null
  );
  const [tableData, setTableData] = useState<any[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortOption, setSortOption] = useState<string>("Last 7 Days");
  const pageSize = 8; // default cards per page
  const [currentPage, setCurrentPage] = useState(1);

  // Pagination helper
  const paginatedPlans = filteredPlans.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );
  useEffect(() => {
    const getPlans = async () => {
      try {
        const data = await fetchPlans();
        setPlans(data);
      } catch (err) {
        console.error("Error fetching plans", err);
      } finally {
        setLoading(false);
      }
    };

    getPlans();

    const loadDashboardData = async () => {
      try {
        const plansData = await fetchPlans();
        setPlans(plansData);
        setFilteredPlans(plansData); // initialize with all
        const formattedPlans = plansData.map((item, idx) => ({
          key: item.plan_id || idx,
          Plan_Name: item.plan_name,
          Plan_Type: item.plan_type,
          Price: item.plan_price?.toString() || "",
          Status: item.status,
          plan_id: item.plan_id,
        }));
        setTableData(formattedPlans);

        const widgetData = await fetchPlanWidgetCounts();
        setWidgetCounts(widgetData);
      } catch (error) {
        console.error("Error loading dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  useEffect(() => {
    let sortedPlans = [...plans];

    switch (sortOption) {
      case "Ascending":
        sortedPlans.sort((a, b) =>
          (a.plan_name || "").localeCompare(b.plan_name || "")
        );
        break;

      case "Descending":
        sortedPlans.sort((a, b) =>
          (b.plan_name || "").localeCompare(a.plan_name || "")
        );
        break;

      case "Last 7 Days":
        sortedPlans = plans.filter((plan) => {
          const createdAt = new Date(plan.created_at || "");
          const now = new Date();
          const diffDays =
            (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24);
          return diffDays <= 7;
        });
        break;

      case "Last Month":
        sortedPlans = plans.filter((plan) => {
          const createdAt = new Date(plan.created_at || "");
          const now = new Date();
          return (
            createdAt.getMonth() === now.getMonth() - 1 &&
            createdAt.getFullYear() === now.getFullYear()
          );
        });
        break;

      case "Recently Added":
        sortedPlans.sort(
          (a, b) =>
            new Date(b.created_at || "").getTime() -
            new Date(a.created_at || "").getTime()
        );
        break;
    }

    const filtered =
      selectedPlanType === "All"
        ? sortedPlans
        : sortedPlans.filter(
            (p) => p.plan_type?.toLowerCase() === selectedPlanType.toLowerCase()
          );

    setFilteredPlans(filtered);
  }, [sortOption, plans, selectedPlanType]);

  const handlePlanFilter = (planType: string) => {
    setSelectedPlanType(planType);
    const filtered =
      planType === "All"
        ? plans
        : plans.filter(
            (p) => p.plan_type?.toLowerCase() === planType.toLowerCase()
          );
    setFilteredPlans(filtered);
  };

  return (
    <>
      <>
        {/* Page Wrapper */}
        <div className="page-wrapper">
          <div className="content">
            <div className="row">
              <div className="col-md-12">
                {/* Page Header */}
                <div className="page-header">
                  <div className="row align-items-center">
                    <div className="col-8">
                      <h4 className="page-title">Packages</h4>
                    </div>
                    <div className="col-4 text-end">
                      <div className="head-icons">
                        <CollapseHeader />
                      </div>
                    </div>
                  </div>
                </div>
                {/* /Page Header */}
                <div className="row">
                  {/* Total Plans */}
                  <div className="col-lg-3 col-md-6 d-flex">
                    <div className="card flex-fill">
                      <div className="card-body d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center overflow-hidden">
                          <div>
                            <p className="fs-12 fw-medium mb-1 text-truncate">
                              Total Plans
                            </p>
                            <h4>{plans.length}</h4>
                          </div>
                        </div>
                        <div>
                          <span className="avatar avatar-lg bg-primary flex-shrink-0">
                            <i className="ti ti-box fs-16" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* /Total Plans */}
                  {/* Total Plans */}
                  <div className="col-lg-3 col-md-6 d-flex">
                    <div className="card flex-fill">
                      <div className="card-body d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center overflow-hidden">
                          <div>
                            <p className="fs-12 fw-medium mb-1 text-truncate">
                              Active Plans
                            </p>
                            <h4>
                              {
                                plans.filter((p) => p.status === "Active")
                                  .length
                              }
                            </h4>
                          </div>
                        </div>
                        <div>
                          <span className="avatar avatar-lg bg-success flex-shrink-0">
                            <i className="ti ti-activity-heartbeat fs-16" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* /Total Plans */}
                  {/* Inactive Plans */}
                  <div className="col-lg-3 col-md-6 d-flex">
                    <div className="card flex-fill">
                      <div className="card-body d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center overflow-hidden">
                          <div>
                            <p className="fs-12 fw-medium mb-1 text-truncate">
                              Inactive Plans
                            </p>
                            <h4>
                              {
                                plans.filter((p) => p.status !== "Active")
                                  .length
                              }
                            </h4>
                          </div>
                        </div>
                        <div>
                          <span className="avatar avatar-lg bg-danger flex-shrink-0">
                            <i className="ti ti-player-pause fs-16" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* /Inactive Companies */}
                  {/* No of Plans  */}
                  <div className="col-lg-3 col-md-6 d-flex">
                    <div className="card flex-fill">
                      <div className="card-body d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center overflow-hidden">
                          <div>
                            <p className="fs-12 fw-medium mb-1 text-truncate">
                              No of Plan Types
                            </p>
                            <h4>
                              {new Set(plans.map((p) => p.plan_type)).size}
                            </h4>
                          </div>
                        </div>
                        <div>
                          <span className="avatar avatar-lg bg-skyblue flex-shrink-0">
                            <i className="ti ti-mask fs-16" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* /No of Plans */}
                </div>
                <div className="card">
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <h5>Plans List</h5>
                      <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                        <div className="dropdown me-3">
                          <Link
                            to="#"
                            className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                            data-bs-toggle="dropdown"
                          >
                            Select Plan: {selectedPlanType}
                          </Link>
                          <ul className="dropdown-menu dropdown-menu-end p-3">
                            <li>
                              <Link
                                to="#"
                                className="dropdown-item rounded-1"
                                onClick={() => handlePlanFilter("All")}
                              >
                                All
                              </Link>
                            </li>
                            {Array.from(
                              new Set(plans.map((p) => p.plan_type))
                            ).map((type) => (
                              <li key={type}>
                                <Link
                                  to="#"
                                  className="dropdown-item rounded-1"
                                  onClick={() => handlePlanFilter(type || "")}
                                >
                                  {type}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="dropdown me-2">
                          <Link
                            to="#"
                            className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                            data-bs-toggle="dropdown"
                          >
                            Sort By : Last 7 Days
                          </Link>
                          <ul className="dropdown-menu dropdown-menu-end p-3">
                            {[
                              "Recently Added",
                              "Ascending",
                              "Descending",
                              "Last Month",
                              "Last 7 Days",
                            ].map((option) => (
                              <li key={option}>
                                <Link
                                  to="#"
                                  className="dropdown-item rounded-1"
                                  onClick={() => setSortOption(option)}
                                >
                                  {option}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="view-icons">
                          <Link to={all_routes.superadminPackagelist}>
                            <i className="ti ti-list-tree" />
                          </Link>
                          <Link
                            to={all_routes.superadminPackageGrid}
                            className="active"
                          >
                            <i className="ti ti-grid-dots" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card">
                  <div className="card-body pb-0">
                    {loading ? (
                      <div className="text-center my-4">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        />
                      </div>
                    ) : (
                      <>
                        <div className="row justify-content-center">
                          {paginatedPlans.map((plan, i) => (
                            <div
                              key={plan.plan_id || i}
                              className="col-lg-3 col-md-6 col-sm-12 d-flex"
                            >
                              <div className="card flex-fill">
                                <div className="card-body">
                                  <h4>{plan.plan_name || `Plan ${i + 1}`}</h4>
                                  <h1 className="mb-1">
                                    {plan.plan_currency || "$"}
                                    {plan.plan_price || "0"}
                                  </h1>
                                  <p className="fs-14 fw-normal text-gray mb-3">
                                    {plan.plan_type || "No plan type"}
                                  </p>
                                  <div className="pricing-content rounded bg-light mb-3 p-3">
                                    <h6 className="fs-14 fw-medium text-gray mb-3">
                                      Features Includes
                                    </h6>
                                    <span className="text-dark d-flex align-items-center mb-2">
                                      <i className="ti ti-discount-check-filled text-success me-2" />
                                      Admins: {plan.no_admin || "N/A"}
                                    </span>
                                    <span className="text-dark d-flex align-items-center mb-2">
                                      <i className="ti ti-discount-check-filled text-success me-2" />
                                      Users: {plan.no_user || "N/A"}
                                    </span>
                                    <span className="text-dark d-flex align-items-center mb-2">
                                      <i className="ti ti-discount-check-filled text-success me-2" />
                                      Trial Days: {plan.trail_days || "0"}
                                    </span>
                                    <span className="text-dark d-flex align-items-center">
                                      <i
                                        className={`ti ${
                                          plan.status === "Active"
                                            ? "ti-check text-success"
                                            : "ti-circle-x-filled text-danger"
                                        } me-2`}
                                      />
                                      Status: {plan.status || "Inactive"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="d-flex justify-content-center mt-4">
                          <Pagination
                            current={currentPage}
                            pageSize={pageSize}
                            total={filteredPlans.length}
                            onChange={(page) => setCurrentPage(page)}
                            showSizeChanger={false}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /Page Wrapper */}
      </>
    </>
  );
};

export default PackagesGrid;
